/**
 * symbolIndex — extracts function/class/type/interface/exported-const names
 * from TypeScript & JavaScript files using a deliberately simple regex pass,
 * caches the result, and keeps it in sync via watcher events.
 *
 * No UI yet — the cache is read on init and updated incrementally so the
 * data is ready when a future Cmd+T "go to symbol in workspace" picker
 * lands. Until then, anyone can use `useSymbolIndexStore.search(...)` to
 * look up symbols by name.
 *
 * Why regex instead of a real TS parser: the goal is "fast enough to be
 * built incrementally on every save without anyone noticing." The TS
 * compiler is correct but adds 1+ MB to the bundle and parses orders of
 * magnitude slower. We accept misclassification on edge cases (computed
 * names, tagged template literals that look like declarations) in exchange
 * for sub-millisecond per-file extraction.
 */
import { create } from 'zustand';
import { logger } from '@/lib/logger';
import { CacheKeys, envelope, readCache, writeCache, type CacheEnvelope } from '@/lib/projectCache';
import { useCacheStatusStore } from '@/stores/cacheStatusStore';
import type { IBackend } from '@/services/backend/IBackend';

const TS_EXT_RE = /\.(tsx?|jsx?|mts|cts)$/i;
/** Same defense-in-depth excludes as monacoTSWorkspace — see ALWAYS_EXCLUDE_SEGMENTS. */
const EXCLUDE_SEGMENTS = [
  'node_modules', '.git', 'target', 'dist', 'build',
  '.next', '.nuxt', '.svelte-kit', '.cache', 'out', '.turbo',
];
const EXCLUDE_RE = new RegExp(
  `(^|/)(?:${EXCLUDE_SEGMENTS.map((s) => s.replace(/\./g, '\\.')).join('|')})(/|$)`,
);
const SYMBOL_CACHE_VERSION = 2; // bumped: previous cache may include node_modules junk

export type SymbolKind = 'function' | 'class' | 'interface' | 'type' | 'const' | 'enum';

export interface SymbolEntry {
  name: string;
  kind: SymbolKind;
  /** 1-indexed line number where the declaration starts. */
  line: number;
  /** Project-relative path (forward slashes). */
  path: string;
  /** True if the declaration is `export`-ed at top level. */
  exported: boolean;
}

interface SymbolIndexCachePayload {
  symbols: SymbolEntry[];
}

interface SymbolIndexState {
  projectId: string | null;
  byPath: Map<string, SymbolEntry[]>;
  /** Init for a project: read cache, then refresh in the background. */
  init: (backend: IBackend, projectId: string, projectRoot: string) => Promise<void>;
  /** Apply a watcher event — re-extract or drop the file's symbols. */
  onFileEvent: (
    backend: IBackend,
    projectId: string,
    projectRoot: string,
    relPath: string,
    changeType: 'created' | 'modified' | 'deleted',
  ) => Promise<void>;
  /** Look up symbols by name (case-insensitive substring). For future Cmd+T. */
  search: (query: string, limit?: number) => SymbolEntry[];
  /** All current symbols flat — for debugging or future "find all" uses. */
  all: () => SymbolEntry[];
  reset: () => void;
}

export const useSymbolIndexStore = create<SymbolIndexState>((set, get) => ({
  projectId: null,
  byPath: new Map(),

  init: async (backend, projectId, projectRoot) => {
    set({ projectId, byPath: new Map() });

    // 1. Cache read for instant availability.
    const cached = await readCache<CacheEnvelope<SymbolIndexCachePayload>>(
      projectId,
      CacheKeys.symbols,
    );
    if (cached && cached.schemaVersion === SYMBOL_CACHE_VERSION) {
      const map = new Map<string, SymbolEntry[]>();
      for (const sym of cached.payload.symbols) {
        const list = map.get(sym.path) ?? [];
        list.push(sym);
        map.set(sym.path, list);
      }
      if (get().projectId === projectId) {
        set({ byPath: map });
        logger.info(
          'FileTreePerf',
          `symbol index from cache: ${cached.payload.symbols.length} symbols across ${map.size} files`,
        );
      }
    }

    // 2. Refresh in background. We re-extract the same files the TS workspace
    //    eagerly loads (capped) so the symbol index covers what intellisense
    //    covers.
    useCacheStatusStore.getState().begin('symbols');
    try {
      const t0 = performance.now();
      const entries = await backend.files.indexProjectFiles(projectId);
      // indexProjectFiles returns ABSOLUTE filesystem paths. Convert to
      // project-relative so joinPath(root, rel) doesn't double the prefix.
      // Also enforce the ALWAYS_EXCLUDE filter so node_modules/etc never
      // get parsed into the symbol index.
      const tsPaths = entries
        .filter((e) => e.type === 'file' && TS_EXT_RE.test(e.path))
        .map((e) => relativize(projectRoot, e.path))
        .filter((p): p is string => p !== null && !EXCLUDE_RE.test(p));
      const map = new Map<string, SymbolEntry[]>();
      // Concurrency cap — same idea as TSWorkspace.loadFiles.
      const CONCURRENCY = 32;
      let i = 0;
      const workers = Array.from({ length: CONCURRENCY }, async () => {
        while (i < tsPaths.length) {
          const idx = i++;
          const rel = tsPaths[idx];
          try {
            const content = await backend.files.readFile(projectId, joinPath(projectRoot, rel));
            const symbols = extractSymbols(rel, content);
            if (symbols.length > 0) map.set(rel, symbols);
          } catch {
            // Skip unreadable files.
          }
        }
      });
      await Promise.all(workers);

      if (get().projectId !== projectId) return;
      set({ byPath: map });

      const total = Array.from(map.values()).reduce((n, arr) => n + arr.length, 0);
      logger.info(
        'FileTreePerf',
        `symbol index refresh: ${(performance.now() - t0).toFixed(0)}ms (${total} symbols across ${map.size} files)`,
      );

      // Persist for next session.
      const flat: SymbolEntry[] = [];
      for (const arr of map.values()) flat.push(...arr);
      await writeCache(
        projectId,
        CacheKeys.symbols,
        envelope<SymbolIndexCachePayload>({ symbols: flat }, SYMBOL_CACHE_VERSION),
      );
    } catch (e) {
      logger.warn('SymbolIndex', 'refresh failed:', e);
    } finally {
      useCacheStatusStore.getState().end('symbols');
    }
  },

  onFileEvent: async (backend, projectId, projectRoot, relPath, changeType) => {
    if (!TS_EXT_RE.test(relPath)) return;
    if (get().projectId !== projectId) return;

    if (changeType === 'deleted') {
      const next = new Map(get().byPath);
      if (next.delete(relPath)) set({ byPath: next });
      return;
    }
    try {
      const content = await backend.files.readFile(projectId, joinPath(projectRoot, relPath));
      const symbols = extractSymbols(relPath, content);
      const next = new Map(get().byPath);
      if (symbols.length > 0) next.set(relPath, symbols);
      else next.delete(relPath);
      set({ byPath: next });
    } catch {
      // Read failed — leave existing symbols in place rather than blanking them.
    }
  },

  search: (query, limit = 50) => {
    const q = query.toLowerCase();
    if (!q.trim()) return [];
    const results: SymbolEntry[] = [];
    for (const arr of get().byPath.values()) {
      for (const sym of arr) {
        if (sym.name.toLowerCase().includes(q)) {
          results.push(sym);
          if (results.length >= limit) return results;
        }
      }
    }
    return results;
  },

  all: () => {
    const out: SymbolEntry[] = [];
    for (const arr of get().byPath.values()) out.push(...arr);
    return out;
  },

  reset: () => set({ projectId: null, byPath: new Map() }),
}));

// ─── Extraction (regex-based) ───────────────────────────────────────────────

const PATTERNS: Array<{ re: RegExp; kind: SymbolKind }> = [
  // function declarations
  { re: /^[\t ]*(?:export\s+(?:default\s+)?(?:async\s+)?)?(?:async\s+)?function\s+([A-Za-z_$][\w$]*)/gm, kind: 'function' },
  // class declarations
  { re: /^[\t ]*(?:export\s+(?:default\s+)?(?:abstract\s+)?)?(?:abstract\s+)?class\s+([A-Za-z_$][\w$]*)/gm, kind: 'class' },
  // interface declarations
  { re: /^[\t ]*(?:export\s+)?interface\s+([A-Za-z_$][\w$]*)/gm, kind: 'interface' },
  // type aliases
  { re: /^[\t ]*(?:export\s+)?type\s+([A-Za-z_$][\w$]*)/gm, kind: 'type' },
  // enums
  { re: /^[\t ]*(?:export\s+)?(?:const\s+)?enum\s+([A-Za-z_$][\w$]*)/gm, kind: 'enum' },
  // top-level exported const / let / var (catches "export const myFn = ...")
  { re: /^[\t ]*export\s+(?:const|let|var)\s+([A-Za-z_$][\w$]*)/gm, kind: 'const' },
];

function extractSymbols(relPath: string, content: string): SymbolEntry[] {
  // Cheap line-offset cache so we can map regex match indices to 1-indexed lines.
  const lineStarts: number[] = [0];
  for (let i = 0; i < content.length; i++) {
    if (content.charCodeAt(i) === 10) lineStarts.push(i + 1);
  }
  const lineFor = (idx: number): number => {
    let lo = 0;
    let hi = lineStarts.length - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (lineStarts[mid] <= idx) lo = mid;
      else hi = mid - 1;
    }
    return lo + 1; // 1-indexed
  };

  const results: SymbolEntry[] = [];
  const seen = new Set<string>(); // dedupe (e.g. function and exported-const overlap)
  for (const { re, kind } of PATTERNS) {
    re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(content)) !== null) {
      const name = m[1];
      const exported = m[0].trimStart().startsWith('export');
      const line = lineFor(m.index);
      const dedupeKey = `${name}:${line}`;
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      results.push({ name, kind, line, path: relPath, exported });
    }
  }
  return results;
}

function joinPath(root: string, relative: string): string {
  const sep = root.includes('\\') && !root.includes('/') ? '\\' : '/';
  const norm = sep === '\\' ? relative.replace(/\//g, '\\') : relative;
  if (root.endsWith('/') || root.endsWith('\\')) return root + norm;
  return root + sep + norm;
}

/**
 * Strip the project root from an absolute path, returning a forward-slash
 * project-relative form. Returns null if the path doesn't live under the
 * root.
 */
function relativize(root: string, abs: string): string | null {
  const normRoot = root.replace(/\\/g, '/').replace(/\/$/, '');
  const normAbs = abs.replace(/\\/g, '/');
  if (normAbs.toLowerCase().startsWith(normRoot.toLowerCase() + '/')) {
    return normAbs.slice(normRoot.length + 1);
  }
  if (!normAbs.includes(':') && !normAbs.startsWith('/')) {
    return normAbs;
  }
  return null;
}
